import React, { useEffect, useState, useMemo } from "react";
import { Box, TextField, InputAdornment, Button } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import LaunchIcon from "@mui/icons-material/Launch";
import { obtenerDatosSites, descargarAprobados } from "../../api/config";
import { GlobalContext } from "../../GlobalContext";
import { useNavigate } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import { useTranslation } from "react-i18next";
import { LoadingButton } from "@mui/lab";
import { DataGrid, GridToolbarContainer } from "@mui/x-data-grid";

const CustomToolbar = ({ searchTerm, onChange, downloading, handleDownloadApproved }) => {
	const { t } = useTranslation();
	return (
		<GridToolbarContainer
			sx={{
				px: 1,
				py: 1,
				borderBottom: 1,
				borderColor: "divider",
				width: "100%",
				display: "flex",
				alignItems: "center",
				gap: 1,
			}}
		>
			{/* Buscador */}
			<Box sx={{ width: "24%", minWidth: 260 }}>
				<TextField
					sx={{ "& .MuiOutlinedInput-root": { borderRadius: "16px" } }}
					fullWidth
					size="small"
					label={t("aux.search")}
					value={searchTerm}
					onChange={onChange}
					type="search"
					InputProps={{
						endAdornment: (
							<InputAdornment position="end">
								<SearchIcon />
							</InputAdornment>
						),
					}}
				/>
			</Box>

			{/* Botones de acciones masivas */}
			<Box sx={{ flex: 1, display: "flex", justifyContent: "flex-end", gap: 1, minWidth: 260 }}>
				<LoadingButton
					loading={downloading}
					onClick={handleDownloadApproved}
					variant="contained"
					disableElevation
				>
					{t("aux.downloadApproved") || "Descargar aprobados"}
				</LoadingButton>

				{/* <Button
					variant="outlined"
					color="primary"
					sx={{ borderRadius: "16px" }}
					onClick={() => onOpenAction("export")}
					disabled={disabledActions}
				>
					Exportar
				</Button>

				<Button
					variant="outlined"
					color="secondary"
					sx={{ borderRadius: "16px" }}
					onClick={() => onOpenAction("config")}
					disabled={disabledActions}
				>
					Configurar
				</Button> */}
			</Box>
		</GridToolbarContainer>
	);
};

const twoDigits = (n) => {
	const num = Number(n);
	return Number.isFinite(num) ? String(Math.abs(num)).padStart(2, "0") : "--";
};
const withSign2 = (n) => {
	const num = Number(n);
	if (!Number.isFinite(num)) return "--";
	return `${num < 0 ? "-" : ""}${twoDigits(num)}`;
};
const covColor = (v) =>
	Number(v) > 0 ? "success.main" : Number(v) === 0 ? "text.secondary" : "error.main";
const holgColor = (v) =>
	Number(v) < 0 ? "success.main" : Number(v) === 0 ? "text.secondary" : "error.main";

const HomeOfHomes = () => {
	const navigate = useNavigate();
	const [searchTermAvailable, setSearchTermAvailable] = useState("");
	const [datos, setDatos] = useState([]);
	const [loading, setLoading] = useState(true);
	const [downloading, setDownloading] = useState(false);

	const { setSiteId } = React.useContext(GlobalContext);
	const { t } = useTranslation();
	const [paginationModel, setPaginationModel] = React.useState({
		pageSize: 10,
		page: 0,
	});

	useEffect(() => {
		const obtenerDatos = async () => {
			try {
				setLoading(true);
				const data = await obtenerDatosSites("/site/get_fully_names");
				setDatos(data);
				setLoading(false);
			} catch (error) {
				console.error("Error en obtenerDatosSites:", error);
			}
		};
		obtenerDatos();
	}, []);

	const filterDatos = (datosIn, searchTerm) => {
		if (Array.isArray(datosIn)) {
			// Si ya viene array, filtramos array
			if (!searchTerm) return datosIn;
			return datosIn.filter((item) =>
				String(item?.full_name || "")
					.toLowerCase()
					.includes(searchTerm),
			);
		}
		if (searchTerm) {
			return (
				Object.keys(datosIn || {})
					.filter(
						(key) =>
							key.toLowerCase().includes(searchTerm) ||
							(datosIn[key].full_name && datosIn[key].full_name.toLowerCase().includes(searchTerm)),
					)
					// eslint-disable-next-line no-sequences
					.reduce((res, key) => ((res[key] = datosIn[key]), res), {})
			);
		} else {
			return datosIn;
		}
	};

	const filteredDatos = useMemo(
		() => filterDatos(datos, searchTermAvailable.toLowerCase()),
		[datos, searchTermAvailable],
	);

	const handleSearchChange = (e) => {
		setSearchTermAvailable(e.target.value);
	};

	const rows = useMemo(() => {
		if (Array.isArray(filteredDatos)) return filteredDatos;
		return Object.keys(filteredDatos || {}).map((k) => filteredDatos[k]);
	}, [filteredDatos]);

	const handleSelect = (data) => {
		const id = data.id;
		setSiteId(id);
		localStorage.setItem("siteId", id);
		localStorage.setItem("siteName", data.full_name);
		navigate("/home");
	};

	const columns = useMemo(
		() => [
			{
				field: "full_name",
				headerName: t("labels.storeOrRegion"),
				flex: 2.5,
				minWidth: 340,
				sortable: true,
				resizable: false,
				renderCell: (params) => (
					<Box
						sx={{
							whiteSpace: "normal",
							overflowWrap: "anywhere",
							wordBreak: "break-word",
							width: "100%",
							lineHeight: 1.25,
							pr: 1,
						}}
					>
						{params.value}
					</Box>
				),
			},
			{
				field: "var_cobertura",
				headerName: t("labels.realCoverageVar"),
				type: "number",
				align: "center",
				headerAlign: "center",
				flex: 1,
				minWidth: 100,
				resizable: false,
				renderCell: (p) => (
					<Box sx={{ fontWeight: 700, color: covColor(p.value) }}>{withSign2(p.value)}</Box>
				),
				sortComparator: (a, b) => {
					const numA = Number(a);
					const numB = Number(b);

					const invalidA = a === "--" || a == null || Number.isNaN(numA);
					const invalidB = b === "--" || b == null || Number.isNaN(numB);

					if (invalidA && invalidB) return 0;
					if (invalidA) return 1;
					if (invalidB) return -1;

					return numA - numB;
				},
			},
			{
				field: "var_holgura",
				headerName: t("labels.slackVar"),
				type: "number",
				align: "center",
				headerAlign: "center",
				flex: 1,
				minWidth: 100,
				resizable: false,
				renderCell: (p) => (
					<Box sx={{ fontWeight: 700, color: holgColor(p.value) }}>{withSign2(p.value)}</Box>
				),
				sortComparator: (a, b) => {
					const numA = Number(a);
					const numB = Number(b);

					const invalidA = a === "--" || a == null || Number.isNaN(numA);
					const invalidB = b === "--" || b == null || Number.isNaN(numB);

					if (invalidA && invalidB) return 0;
					if (invalidA) return 1;
					if (invalidB) return -1;

					return numA - numB;
				},
			},
			{
				field: "__acciones",
				headerName: "",
				sortable: false,
				filterable: false,
				align: "right",
				headerAlign: "right",
				flex: 1.5,
				width: 160,
				resizable: false,
				renderCell: (p) => (
					<Button
						size="small"
						endIcon={<LaunchIcon />}
						onClick={() => handleSelect(p.row)} // <== MISMA ACCIÓN QUE LA LISTA
					>
						{t("aux.viewDetail")}
					</Button>
				),
			},
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[t],
	);

	const getFilenameFromDisposition = (dispo) => {
		if (!dispo) return null;
		// filename*=UTF-8''...  ó  filename="..."
		const star = dispo.match(/filename\*=UTF-8''([^;]+)/i);
		if (star && star[1]) return decodeURIComponent(star[1]);
		const plain = dispo.match(/filename="?([^"]+)"?/i);
		if (plain && plain[1]) return plain[1];
		return null;
	};

	const guessExcelExt = (arrayBuffer, contentType = "") => {
		const u8 = new Uint8Array(arrayBuffer.slice(0, 8));
		const startsWith = (arr) => arr.every((b, i) => u8[i] === b);

		// por Content-Type
		if (/sheet|excel/i.test(contentType)) return ".xlsx";
		if (/csv/i.test(contentType)) return ".csv";

		// por firma
		if (startsWith([0x50, 0x4b])) return ".xlsx"; // ZIP (xlsx)
		if (startsWith([0xd0, 0xcf, 0x11, 0xe0])) return ".xls"; // OLE (xls)

		return ".bin";
	};

	const guessMimeByExt = (ext) => {
		switch (ext) {
			case ".xlsx":
				return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
			case ".xls":
				return "application/vnd.ms-excel";
			case ".csv":
				return "text/csv";
			default:
				return "application/octet-stream";
		}
	};

	const handleDownloadApproved = async () => {
		setDownloading(true);
		try {
			const resp = await descargarAprobados(); // arraybuffer

			if (resp.status === 200) {
				const dispo = resp.headers?.["content-disposition"];
				const ctype = resp.headers?.["content-type"] || "";

				let filename = getFilenameFromDisposition(dispo);

				const ext = guessExcelExt(resp.data, ctype);
				if (!filename) {
					filename = `approved_outputs_${new Date().toISOString().slice(0, 10)}${ext}`;
				} else if (!/\.[A-Za-z0-9]+$/.test(filename)) {
					filename += ext;
				}

				const mime = guessMimeByExt(ext);
				const blob = new Blob([resp.data], { type: mime });

				const url = window.URL.createObjectURL(blob);
				const a = document.createElement("a");
				a.href = url;
				a.download = filename;
				document.body.appendChild(a);
				a.click();
				a.remove();
				window.URL.revokeObjectURL(url);
			} else {
				console.error("Descarga falló con status:", resp.status);
			}
		} catch (e) {
			console.error("Error al descargar aprobados:", e);
		} finally {
			setDownloading(false);
		}
	};

	return (
		<Box
			sx={{
				width: "95%",
				height: "80vh",
				border: 1,
				borderColor: "secondary.main",

				justifyContent: "space-between",
				borderRadius: "20px",
				overflow: "hidden",
			}}
		>
			{loading ? (
				<Box
					sx={{
						width: "100%",
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
					}}
				>
					<CircularProgress color="primary" />
				</Box>
			) : (
				<>
					<Box sx={{ width: "100%" }} />
					<DataGrid
						rows={rows}
						getRowId={(row) => row.id ?? `${row.full_name}-${row.period ?? ""}`}
						columns={columns}
						paginationModel={paginationModel}
						onPaginationModelChange={setPaginationModel}
						pagination={paginationModel.pageSize !== -1}
						pageSizeOptions={[10, 25, 50, { label: "Ver todos", value: -1 }]}
						disableColumnResize
						rowHeight={52}
						slots={{ toolbar: CustomToolbar }}
						slotProps={{
							toolbar: {
								searchTerm: searchTermAvailable,
								onChange: handleSearchChange,
								label: t("aux.search"),
								downloading,
								handleDownloadApproved,
							},
						}}
						sx={{
							border: 0,
							"& .MuiDataGrid-columnHeaders": { bgcolor: "#faf7f5" },
							"& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
								outline: "none",
							},
						}}
					/>
				</>
			)}
		</Box>
	);
};

export { HomeOfHomes };
